'use client';
import { useEffect, useState } from 'react';
import { fetchWithCache } from '@/lib/use-resource';
import { SlideFrame } from '../SlideFrame';

interface PortfolioResp {
  symbols: string[];
  weights: Record<string, number>;
  cashWeight: number;
  entryPrices?: Record<string, number>;
  /** Positions that pre-date the alpha-zone signal. The model doesn't take
   *  credit for them (Steve 2026-05-15). They show in the table for context
   *  but are excluded from headline counts + the model's contribution total. */
  legacyHoldings?: string[];
}
interface PnlEntry { ytdPct: number | null }
interface PnlResp { bySymbol: Record<string, PnlEntry> }
interface BasketSlot { current: { symbol: string; name?: string; sector?: string } }
interface BasketResp { basket?: { slots?: BasketSlot[] }; displaySlots?: BasketSlot[] }
interface ApiStock { symbol: string; name?: string; price?: number }
interface StocksResp { stocks?: ApiStock[] }

interface Row {
  symbol: string;
  name: string;
  weightPct: number;
  entryPrice: number | null;
  currentPrice: number | null;
  sinceEntryPct: number | null;
  ytdPct: number;
  contributionPct: number; // pp — weight × since-entry (model contribution)
  legacy: boolean;
}

const fmtPct = (v: number, digits = 1) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(digits)}%`;
const fmtPp = (v: number, digits = 2) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(digits)}pp`;
const colorOf = (v: number) => (v < 0 ? 'text-red-400' : 'text-emerald-400');

// Steve's actual book YTD return. Hardcoded because the model-side math
// (weight × ytd-of-each-stock) can't capture trades, currency, and fees.
// Update when the book moves materially. Source: Steve.
const ACTUAL_BOOK_YTD_PP = 2.8;

export function Slide15AlphaZoneInPortfolio() {
  const [data, setData] = useState<{
    rows: Row[];
    totalYtdPp: number;
    alphaContribPp: number;
    restContribPp: number;
    alphaWeightPct: number;
  } | null>(null);

  useEffect(() => {
    Promise.all([
      fetchWithCache<PortfolioResp>('/api/portfolio'),
      fetchWithCache<PnlResp>('/api/holdings-pnl'),
      fetchWithCache<BasketResp>('/api/alpha-basket'),
      fetchWithCache<StocksResp>('/api/stocks'),
    ]).then(([p, pnl, basket, stocks]) => {
      const basketSlots = basket?.displaySlots ?? basket?.basket?.slots ?? [];
      const basketSyms = new Set(basketSlots.map(s => s.current.symbol));
      const nameBy = new Map<string, string>();
      const priceBy = new Map<string, number>();
      for (const s of stocks?.stocks ?? []) {
        if (!s.symbol) continue;
        nameBy.set(s.symbol, s.name ?? s.symbol);
        if (s.price != null && Number.isFinite(s.price)) priceBy.set(s.symbol, s.price);
      }
      const entryPrices = p.entryPrices ?? {};
      const legacy = new Set(p.legacyHoldings ?? []);

      const rows: Row[] = [];
      let modelContrib = 0; // alpha-zone contribution we DO claim (since-entry × weight, legacy excluded)
      let modelW = 0;       // alpha-zone weight we DO claim (legacy excluded)
      for (const sym of p.symbols) {
        const w = p.weights[sym];
        const ytd = pnl?.bySymbol?.[sym]?.ytdPct;
        if (w == null || ytd == null) continue;
        const entryPrice = entryPrices[sym] ?? null;
        const currentPrice = priceBy.get(sym) ?? null;
        const sinceEntryPct =
          entryPrice != null && entryPrice > 0 && currentPrice != null
            ? ((currentPrice / entryPrice) - 1) * 100
            : null;
        if (basketSyms.has(sym)) {
          // Model contribution = weight × since-entry-return. This is honest:
          // it credits the model with the move from the day Steve acted on
          // the signal, not from Jan 1.
          const isLegacy = legacy.has(sym);
          const sinceContrib = sinceEntryPct != null ? (w * sinceEntryPct) : 0;
          if (!isLegacy) {
            modelContrib += sinceContrib;
            modelW += w;
          }
          rows.push({
            symbol: sym,
            name: (nameBy.get(sym) ?? sym).split(' ')[0],
            weightPct: w * 100,
            entryPrice,
            currentPrice,
            sinceEntryPct,
            ytdPct: ytd * 100,
            contributionPct: sinceContrib,
            legacy: isLegacy,
          });
        }
      }
      // Sort: non-legacy first (by contribution desc), then legacy at the bottom.
      rows.sort((a, b) => {
        if (a.legacy !== b.legacy) return a.legacy ? 1 : -1;
        return b.contributionPct - a.contributionPct;
      });
      setData({
        rows,
        totalYtdPp: ACTUAL_BOOK_YTD_PP,
        alphaContribPp: modelContrib,
        restContribPp: ACTUAL_BOOK_YTD_PP - modelContrib,
        alphaWeightPct: modelW * 100,
      });
    }).catch(() => {});
  }, []);

  return (
    <SlideFrame pageNum={19} title="ALPHA ZONE IN THE PORTFOLIO"
      banner="Names sourced from the model and their contribution to year-to-date returns.">
      <div className="h-full flex flex-col gap-3 pt-1 pb-10">

        {/* Headline: how much of YTD came from alpha-zone names */}
        <div className="grid grid-cols-3 gap-3">
          <HeadlineCard
            label="Alpha Zone companies in portfolio"
            big={data ? `${data.rows.length}` : '—'}
            sub={data ? `${data.alphaWeightPct.toFixed(1)}% of book` : ''}
            tone="gold"
          />
          <HeadlineCard
            label="Their contribution since entry"
            big={data ? fmtPp(data.alphaContribPp, 1) : '—'}
            sub="weight × since-entry return"
            tone="emerald"
          />
          <HeadlineCard
            label="Rest of book contribution"
            big={data ? fmtPp(data.restContribPp, 1) : '—'}
            sub={data ? `from ${data.rows.filter(r => !r.legacy).length > 0 ? (16 - data.rows.filter(r => !r.legacy).length) : 16} other holdings (YTD)` : ''}
            tone="slate"
          />
        </div>

        {/* Per-name table */}
        <div className="bg-[#1e293b] rounded-2xl overflow-hidden flex-1">
          <table className="w-full text-[13px] tabular-nums">
            <thead>
              <tr className="text-[#94a3b8] text-[10px] tracking-widest uppercase border-b border-[#334155]">
                <th className="px-3 py-2 text-left font-semibold w-[100px]">Symbol</th>
                <th className="px-3 py-2 text-left font-semibold">Company</th>
                <th className="px-3 py-2 text-right font-semibold">Weight</th>
                <th className="px-3 py-2 text-right font-semibold">Entry</th>
                <th className="px-3 py-2 text-right font-semibold">Since entry</th>
                <th className="px-3 py-2 text-right font-semibold">YTD</th>
                <th className="px-3 py-2 text-right font-semibold border-l border-[#334155]">Contribution<br/><span className="text-[8px] normal-case tracking-normal text-[#64748b]">since entry</span></th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((r, i) => (
                <tr key={r.symbol} className={i > 0 ? 'border-t border-[#334155]/60' : ''}>
                  <td className={`px-3 py-2 font-bold ${r.legacy ? 'text-[#64748b]' : 'text-[#c9a84c]'}`}>
                    {r.symbol}
                    {r.legacy && <span className="block text-[8px] text-[#475569] uppercase tracking-wider font-normal mt-0.5">held before</span>}
                  </td>
                  <td className={`px-3 py-2 ${r.legacy ? 'text-[#64748b]' : 'text-[#cbd5e1]'}`}>{r.name}</td>
                  <td className={`px-3 py-2 text-right ${r.legacy ? 'text-[#64748b]' : 'text-white'}`}>{r.weightPct.toFixed(1)}%</td>
                  <td className={`px-3 py-2 text-right ${r.legacy ? 'text-[#64748b]' : 'text-[#cbd5e1]'}`}>
                    {r.entryPrice != null ? `$${r.entryPrice.toFixed(2)}` : '—'}
                  </td>
                  <td className={`px-3 py-2 text-right font-semibold ${r.sinceEntryPct != null ? colorOf(r.sinceEntryPct) : 'text-[#64748b]'} ${r.legacy ? 'opacity-60' : ''}`}>
                    {r.sinceEntryPct != null ? fmtPct(r.sinceEntryPct) : '—'}
                  </td>
                  <td className={`px-3 py-2 text-right ${colorOf(r.ytdPct)} ${r.legacy ? 'opacity-60' : ''}`}>{fmtPct(r.ytdPct)}</td>
                  <td className={`px-3 py-2 text-right font-bold border-l border-[#334155] ${r.legacy ? 'text-[#64748b]' : colorOf(r.contributionPct)}`}>
                    {r.legacy ? 'not credited' : fmtPp(r.contributionPct, 2)}
                  </td>
                </tr>
              ))}
              {data && (
                <tr className="border-t-2 border-[#c9a84c]/40 bg-[#c9a84c]/5">
                  <td className="px-3 py-2 font-bold text-[#c9a84c]" colSpan={2}>ALPHA ZONE TOTAL</td>
                  <td className="px-3 py-2 text-right text-white font-semibold">{data.alphaWeightPct.toFixed(1)}%</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                  <td className={`px-3 py-2 text-right font-bold border-l border-[#334155] ${colorOf(data.alphaContribPp)}`}>{fmtPp(data.alphaContribPp, 2)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="text-[#94a3b8] text-[10px] italic px-1">
          Alpha Zone = top 10 companies by composite, refreshed daily. Contribution = portfolio weight × year-to-date return.
        </p>

      </div>
    </SlideFrame>
  );
}

function HeadlineCard({ label, big, sub, tone }: { label: string; big: string; sub: string; tone: 'gold' | 'emerald' | 'slate' }) {
  const styles = {
    gold:    { bg: 'bg-[#c9a84c]/15 border-[#c9a84c]/50', big: 'text-[#c9a84c]', label: 'text-[#c9a84c]' },
    emerald: { bg: 'bg-emerald-500/10 border-emerald-500/40', big: 'text-emerald-300', label: 'text-emerald-300' },
    slate:   { bg: 'bg-[#1e293b] border-[#334155]', big: 'text-white', label: 'text-[#94a3b8]' },
  }[tone];
  return (
    <div className={`rounded-2xl border p-3 ${styles.bg}`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold mb-1 ${styles.label}`}>{label}</div>
      <div className={`text-[36px] font-bold tabular-nums leading-none ${styles.big}`}>{big}</div>
      <div className="text-[#94a3b8] text-[11px] mt-1.5">{sub || ' '}</div>
    </div>
  );
}
